import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { TRootState } from '..'
import { apiStateSelectors } from '../ApiStateStore'
import { FilteredCreditApplication } from '../../types/FilteredCreditApplication'
import { RequiredDocument } from '../../types/RequiredDocument'
import { FilteredWorksheet } from '../../types/FilteredWorksheet'
import { RefinancingCreditApplication } from '../../types/RefinancingCreditApplication'
import { MerchantDto } from '../../types/Merchant'
import { CreditApplication } from '../../schemas/PrequalificationSchema'
import { PersonalLoanFundingComputedDto } from '../../types/PersonalLoanFundingComputedDto'
import { AvailableCredit } from '../../types/AvailableCredit'
import { ComputedAmountForFrequencyDto } from '../../types/ComputedAmountForFrequencyDto'
import { CustomerFeedback } from '../../types/CustomerFeedback'

export const CreditApplicationSlice = createSlice({
  name: 'creditApplication',
  initialState: {
    creditApplication: null as CreditApplication | null,
    filteredCreditApplication: null as FilteredCreditApplication | null,
    refinancingCreditApplication: null as RefinancingCreditApplication | null,
    merchant: null as MerchantDto | null,
    currentMerchantId: Number(process.env.REACT_APP_IFINANCE_MERCHANT_ID) as number,
    computedInfo: {} as PersonalLoanFundingComputedDto,
    availableCredit: null as AvailableCredit | null,
    customerFeedback: null as CustomerFeedback | null,
    computedAmounts: null as ComputedAmountForFrequencyDto | null,
    justGaveFeedback: null as CustomerFeedback | null,
  },

  reducers: {
    setCreditApplication: (state, action: PayloadAction<CreditApplication | null>) => {
      state.creditApplication = action.payload
    },
    setFilteredCreditApplication: (state, action: PayloadAction<FilteredCreditApplication | null>) => {
      state.filteredCreditApplication = action.payload
      state.currentMerchantId = Number(state.filteredCreditApplication?.merchantId.split('*')[1])
    },
    setRequiredDocument: (state, action: PayloadAction<RequiredDocument>) => {
      const index = state.filteredCreditApplication?.requiredDocuments.findIndex((x) => x.id === action.payload.id) ?? 0
      state.filteredCreditApplication?.requiredDocuments.splice(index, 1, action.payload)
    },
    setWorkSheet: (state, action: PayloadAction<FilteredWorksheet>) => {
      state.filteredCreditApplication = {
        ...state.filteredCreditApplication,
        worksheet: action.payload,
      } as FilteredCreditApplication
    },
    setRefinancingCreditApplication: (state, action: PayloadAction<RefinancingCreditApplication | null>) => {
      state.refinancingCreditApplication = action.payload
    },
    setMerchant: (state, action: PayloadAction<MerchantDto | null>) => {
      state.merchant = action.payload
      state.currentMerchantId = Number(state.merchant?.id.split('*')[1])
    },
    setFundingComputed: (state, action: PayloadAction<PersonalLoanFundingComputedDto | null>) => {
      if (action.payload)
        state.computedInfo = {
          ...state.computedInfo,
          ...action.payload,
        }
    },
    setAvailableCredit: (state, action: PayloadAction<AvailableCredit | null>) => {
      state.availableCredit = action.payload
    },
    setComputedAmountForFrequency: (state, action: PayloadAction<ComputedAmountForFrequencyDto | null>) => {
      state.computedAmounts = action.payload
    },
    setCustomerFeedback: (state, action: PayloadAction<CustomerFeedback | null>) => {
      state.customerFeedback = action.payload
    },
    setJustGaveFeedback: (state, action: PayloadAction<CustomerFeedback | null>) => {
      state.justGaveFeedback = action.payload
    },
  },
})

// Action creators are generated for each case reducer function
export const creditApplicationActions = CreditApplicationSlice.actions

export default CreditApplicationSlice.reducer

export const POST_DRAFT_CREDIT_APPLICATION_AND_PREQUALIFY = 'creditApplication/postDraftCreditApplicationAndPrequalify'
export const GET_FILTERED_CREDIT_APPLICATION = 'creditApplication/getFilteredCreditApplication'
export const POST_DOCUMENT = 'creditApplication/postDocument'
export const POST_EMAIL_RESUME_LINK = 'creditApplication/postEmailResumeLink'
export const POST_QUALIFY = 'creditApplication/qualify'
export const POST_PROMO_CODE = 'creditApplication/promoCode'
export const POST_COAPPLICANT = 'creditApplication/coapplicant'
export const PUT_CANCEL_CREDIT_APPLICATION = 'creditApplication/putCancelCreditApplication'
export const PUT_UPDATE_CREDIT_APPLICATION = 'creditApplication/putUpdateCreditApplication'
export const GET_REFINANCING_CREDIT_APPLICATION = 'creditApplication/getRefinancingCreditApplication'
export const POST_REFINANCING_CREDIT_APPLICATION = 'creditApplication/postRefinancingCreditApplication'
export const SAVE_WORKSHEET = 'creditApplication/postWorksheet'
export const COMPUTE_PERSONAL_LOAN_FUNDING = 'creditApplication/computePersonalLoanFunding'
export const COMPUTE_AMOUNT_FREQUENCY = 'creditApplication/computeAmountForFrequency'
export const GET_MERCHANT = 'merchant/get'
export const POST_RESEND_ELECTRONIC_SIGNATURE = 'creditApplication/resendElectronicSignature'
export const GET_AVAILABLE_CREDIT = 'creditApplication/getRefinancingCreditApplication'
export const REMOVE_CO_APPLICANT = 'creditApplication/removeCoApplicant'
export const POST_CUSTOMER_FEEDBACK = 'api/{creditApplicationId}/CustomerFeedback'
export const GET_CUSTOMER_FEEDBACK = 'api/{creditApplicationid}/CustomerFeedback'

export const creditApplicationSelectors = {
  getCreditApplication: (state: TRootState) => state.creditApplication.creditApplication,
  getFilteredCreditApplication: (state: TRootState) => state.creditApplication.filteredCreditApplication,
  getRefinancingCreditApplication: (state: TRootState) => state.creditApplication.refinancingCreditApplication,
  getMerchant: (state: TRootState) => state.creditApplication.merchant,
  isLoadingFinalDecision: (state: TRootState) => apiStateSelectors.isLoading(state, POST_QUALIFY),
  isCreatingDraft: (state: TRootState) =>
    apiStateSelectors.isLoading(state, POST_DRAFT_CREDIT_APPLICATION_AND_PREQUALIFY),
  isLoading: (state: TRootState) =>
    apiStateSelectors.isLoading(state, GET_FILTERED_CREDIT_APPLICATION) ||
    apiStateSelectors.isLoading(state, SAVE_WORKSHEET) ||
    apiStateSelectors.isLoading(state, GET_AVAILABLE_CREDIT),
  validatePromoCodeLoading: (state: TRootState) => apiStateSelectors.isLoading(state, POST_PROMO_CODE),
  RefinancingCreditApplicationIsLoading: (state: TRootState) =>
    apiStateSelectors.isLoading(state, GET_REFINANCING_CREDIT_APPLICATION),
  MerchantIsLoading: (state: TRootState) => apiStateSelectors.isLoading(state, GET_MERCHANT),
  getComputedInfo: (state: TRootState) => state.creditApplication.computedInfo,
  getAvailableCredit: (state: TRootState) => state.creditApplication.availableCredit,
  getCurrentMerchantId: (state: TRootState) => state.creditApplication.currentMerchantId,
  getMerchantSupportsPromotions: (state: TRootState) => state.creditApplication.merchant?.supportsPromotions,
  isComputingLoan: (state: TRootState) => apiStateSelectors.isLoading(state, COMPUTE_PERSONAL_LOAN_FUNDING),
  getApiClient: createSelector(
    (state: TRootState) => state.app.axiosBaseConfig,
    (config) => {
      return axios.create(config)
    },
  ),
  getComputedAmounts: (state: TRootState) => state.creditApplication.computedAmounts,
  getCustomerFeedback: (state: TRootState) => state.creditApplication.customerFeedback,
  getJustGaveFeedback: (state: TRootState) => state.creditApplication.justGaveFeedback,
}
